import API_CONFIG from '../config/api';
import authService from './authService';

class ApiService {
  constructor() {
    this.baseURL = API_CONFIG.BASE_URL;
    this.timeout = API_CONFIG.TIMEOUT;
    this.defaultHeaders = { ...API_CONFIG.DEFAULT_HEADERS };
  }

  /**
   * Make a generic API request
   * @param {string} endpoint - API endpoint path
   * @param {Object} options - Request options
   * @param {string} options.method - HTTP method (GET, POST, PUT, DELETE)
   * @param {Object} options.data - Request body data
   * @param {Object} options.headers - Additional headers
   * @param {string} options.apiKey - API key to use (optional)
   * @returns {Promise} - API response
   */
  async request(endpoint, options = {}) {
    const {
      method = 'GET',
      data = null,
      headers = {},
      apiKey = null
    } = options;

    const url = `${this.baseURL}${endpoint}`;
    
    // Prepare headers
    const requestHeaders = { ...this.defaultHeaders, ...headers };
    
    // Add API key if provided
    if (apiKey) {
      requestHeaders['X-University-Key'] = apiKey;
    }

    // Add authorization header if user is authenticated
    const authHeader = authService.getAuthHeader();
    Object.assign(requestHeaders, authHeader);

    // Prepare request config
    const config = {
      method,
      headers: requestHeaders,
      signal: AbortSignal.timeout(this.timeout)
    };

    // Add body for non-GET requests
    if (data && method !== 'GET') {
      // Don't stringify FormData - let browser handle it
      if (data instanceof FormData) {
        config.body = data;
        // Remove Content-Type header for FormData - browser will set it with boundary
        delete requestHeaders['Content-Type'];
      } else {
        config.body = JSON.stringify(data);
      }
    }

    try {
      const response = await fetch(url, config);
      
      // Parse response
      let responseData;
      const contentType = response.headers.get('content-type');
      
      if (contentType && contentType.includes('application/json')) {
        responseData = await response.json();
      } else {
        responseData = await response.text();
      }

      return {
        success: response.ok,
        status: response.status,
        data: responseData,
        headers: response.headers
      };
    } catch (error) {
      if (error.name === 'AbortError') {
        throw new Error('Request timeout');
      }
      throw error;
    }
  }

  /**
   * Student Login API
   * @param {Object} credentials - Login credentials
   * @param {string} credentials.student_id - Student ID
   * @param {string} credentials.password - Password
   * @returns {Promise} - Login response
   */
  async studentLogin(credentials) {
    return this.request(API_CONFIG.ENDPOINTS.STUDENT_LOGIN, {
      method: 'POST',
      data: credentials,
      apiKey: API_CONFIG.KEYS.UNIVERSITY_KEY
    });
  }

  /**
   * Student Registration API
   * @param {Object} studentData - Student registration data
   * @returns {Promise} - Registration response
   */
  async studentRegister(studentData) {
    return this.request(API_CONFIG.ENDPOINTS.STUDENT_REGISTER, {
      method: 'POST',
      data: studentData,
      apiKey: API_CONFIG.KEYS.UNIVERSITY_KEY
    });
  }

  /**
   * Get Student Profile API
   * @param {string} studentId - Student ID
   * @returns {Promise} - Profile response
   */
  async getStudentProfile(studentId) {
    return this.request(`${API_CONFIG.ENDPOINTS.STUDENT_PROFILE}/${studentId}`, {
      method: 'GET',
      apiKey: API_CONFIG.KEYS.UNIVERSITY_KEY
    });
  }

  /**
   * Get Districts API
   * @param {number} stateId - State ID
   * @returns {Promise} - Districts response
   */
  async getDistricts(stateId) {
    return this.request(`${API_CONFIG.ENDPOINTS.DISTRICTS}?state_id=${stateId}`, {
      method: 'GET',
      apiKey: API_CONFIG.KEYS.UNIVERSITY_KEY
    });
  }

  /**
   * Get States API
   * @returns {Promise} - States response
   */
  async getStates() {
    return this.request(API_CONFIG.ENDPOINTS.STATES, {
      method: 'GET',
      apiKey: API_CONFIG.KEYS.UNIVERSITY_KEY
    });
  }

  /**
   * Get Student Step1 Data API
   * @returns {Promise} - Step1 data response
   */
  async getStudentStep1() {
    return this.request(API_CONFIG.ENDPOINTS.STUDENT_STEP1, {
      method: 'GET',
      apiKey: API_CONFIG.KEYS.UNIVERSITY_KEY
    });
  }

  /**
   * Get Admission Sessions API
   * @returns {Promise} - Admission sessions response
   */
  async getAdmissionSessions() {
    return this.request('/api/university/admission-sessions', {
      method: 'GET',
      apiKey: API_CONFIG.KEYS.UNIVERSITY_KEY
    });
  }

  /**
   * Get University Courses API
   * @returns {Promise} - University courses response
   */
  async getUniversityCourses() {
    return this.request('/api/university/leads/university-courses', {
      method: 'GET',
      apiKey: API_CONFIG.KEYS.UNIVERSITY_KEY
    });
  }

  /**
   * Get Course Specializations API
   * @param {number} courseId - Course ID
   * @returns {Promise} - Specializations response
   */
  async getCourseSpecializations(courseId) {
    return this.request(`/api/leads/specializations?course_id=${courseId}`, {
      method: 'GET',
      apiKey: API_CONFIG.KEYS.UNIVERSITY_KEY
    });
  }

  /**
   * Get Payment Structures API
   * @param {number} courseId - Course ID
   * @returns {Promise} - Payment structures response
   */
  async getPaymentStructures(courseId) {
    return this.request(`/api/university/payment-structures?course_id=${courseId}`, {
      method: 'GET',
      apiKey: API_CONFIG.KEYS.UNIVERSITY_KEY
    });
  }

  /**
   * Initiate Payment API
   * @param {Object} paymentData - Payment initiation data
   * @returns {Promise} - Payment initiation response
   */
  async initiatePayment(paymentData) {
    return this.request('/api/payments/initiate', {
      method: 'POST',
      data: paymentData,
      apiKey: API_CONFIG.KEYS.UNIVERSITY_KEY
    });
  }

  /**
   * Verify Payment API
   * @param {Object} verificationData - Payment verification data
   * @returns {Promise} - Payment verification response
   */
  async verifyPayment(verificationData) {
    return this.request('/api/payments/verify', {
      method: 'POST',
      data: verificationData,
      apiKey: API_CONFIG.KEYS.UNIVERSITY_KEY
    });
  }

  /**
   * Initiate EASEBUZZ Payment API
   * @param {Object} paymentData - Payment initiation data
   * @returns {Promise} - EASEBUZZ payment initiation response with access token
   */
  async initiateEasebuzzPayment(paymentData) {
    return this.request('/api/easebuzz/initiate', {
      method: 'POST',
      data: paymentData,
      apiKey: API_CONFIG.KEYS.UNIVERSITY_KEY
    });
  }

  /**
   * Verify EASEBUZZ Payment API
   * @param {Object} verificationData - Payment verification data
   * @returns {Promise} - Payment verification response
   */
  async verifyEasebuzzPayment(verificationData) {
    return this.request('/api/easebuzz/verify', {
      method: 'POST',
      data: verificationData,
      apiKey: API_CONFIG.KEYS.UNIVERSITY_KEY
    });
  }

  // Academic Records API
  async submitAcademicRecords(academicData) {
    return this.request('/api/students/academics', {
      method: 'POST',
      data: academicData,
      apiKey: API_CONFIG.KEYS.UNIVERSITY_KEY
    });
  }

  /**
   * Get Academic Records API
   * @returns {Promise} - Academic records response
   */
  async getAcademicRecords() {
    return this.request('/api/students/academics', {
      method: 'GET',
      apiKey: API_CONFIG.KEYS.UNIVERSITY_KEY
    });
  }

  /**
   * Submit Application API
   * @param {Object} applicationData - Application form data
   * @returns {Promise} - Submission response
   */
  async submitApplication(applicationData) {
    return this.request(API_CONFIG.ENDPOINTS.APPLICATION_SUBMIT, {
      method: 'POST',
      data: applicationData,
      apiKey: API_CONFIG.KEYS.UNIVERSITY_KEY
    });
  }

  /**
   * Submit Lead API
   * @param {Object} leadData - Lead submission data
   * @param {number} leadData.partner_id - Partner ID
   * @param {string} leadData.full_name - Full name
   * @param {string} leadData.email - Email address
   * @param {string} leadData.mobile - Mobile number
   * @param {number} leadData.course_id - Course ID
   * @param {number} leadData.specialization_id - Specialization ID
   * @param {string} leadData.employment_status - Employment status
   * @returns {Promise} - Lead submission response
   */
  async submitLead(leadData) {
    return this.request(API_CONFIG.ENDPOINTS.LEAD_SUBMISSION, {
      method: 'POST',
      data: leadData,
      apiKey: API_CONFIG.KEYS.UNIVERSITY_KEY
    });
  }

  /**
   * Get Application Status API
   * @param {string} applicationId - Application ID
   * @returns {Promise} - Status response
   */
  async getApplicationStatus(applicationId) {
    return this.request(`${API_CONFIG.ENDPOINTS.APPLICATION_STATUS}/${applicationId}`, {
      method: 'GET',
      apiKey: API_CONFIG.KEYS.UNIVERSITY_KEY
    });
  }

  /**
   * Get Post Office Names API
   * @param {string} pincode - Pincode
   * @returns {Promise} - Post office names response
   */
  async getPostOfficeNames(pincode) {
    return this.request(`/api/locations/office-names?pincode=${pincode}`, {
      method: 'GET',
      apiKey: API_CONFIG.KEYS.UNIVERSITY_KEY
    });
  }

  /**
   * Submit Step 1 Data API
   * @param {Object} step1Data - Step 1 form data
   * @returns {Promise} - Step 1 submission response
   */
  async submitStep1(step1Data) {
    return this.request('/api/students/step1', {
      method: 'POST',
      data: step1Data,
      apiKey: API_CONFIG.KEYS.UNIVERSITY_KEY
    });
  }

  /**
   * Submit Documents API
   * @param {Object} documentData - Document submission data
   * @param {string} documentData.file_url - File URL
   * @param {string} documentData.document_type - Document type
   * @param {string} documentData.document_name - Document name
   * @param {number} documentData.sort_order - Sort order
   * @returns {Promise} - Document submission response
   */
  async submitDocuments(documentData) {
    return this.request('/api/students/documents', {
      method: 'POST',
      data: documentData,
      apiKey: API_CONFIG.KEYS.UNIVERSITY_KEY
    });
  }

  /**
   * Update Document API
   * @param {number} documentId - Document ID to update
   * @param {Object} documentData - Document update data
   * @param {string} documentData.file_path - New file path/URL
   * @param {string} documentData.document_type - Document type
   * @param {string} documentData.document_name - Document name
   * @returns {Promise} - Document update response
   */
  async updateDocument(documentId, documentData) {
    return this.request(`/api/students/documents/${documentId}`, {
      method: 'PUT',
      data: documentData,
      apiKey: API_CONFIG.KEYS.UNIVERSITY_KEY
    });
  }

  /**
   * Get Student Documents API
   * @returns {Promise} - Documents response
   */
  async getDocuments() {
    return this.request('/api/students/documents', {
      method: 'GET',
      apiKey: API_CONFIG.KEYS.UNIVERSITY_KEY
    });
  }

  /**
   * Get Student Payments API
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number
   * @param {number} params.per_page - Items per page
   * @param {string} params.status - Payment status filter
   * @returns {Promise} - Payments response
   */
  async getPayments(params = {}) {
    //const queryString = new URLSearchParams(params).toString();
    const endpoint = '/api/students/payments/with-invoices?with_invoices_only=true';
    
    return this.request(endpoint, {
      method: 'GET',
      apiKey: API_CONFIG.KEYS.UNIVERSITY_KEY
    });
  }

  /**
   * Get Student Course Fees API
   * @returns {Promise} - Course fees response
   */
  async getCourseFees() {
    return this.request(API_CONFIG.ENDPOINTS.COURSE_FEES, {
      method: 'GET',
      apiKey: API_CONFIG.KEYS.UNIVERSITY_KEY
    });
  }

  // Helper method to handle API errors consistently
  handleApiError(error, response) {
    if (response && !response.success) {
      return response.data.message || 'An error occurred';
    }
    
    if (error.message === 'Request timeout') {
      return 'Request timed out. Please try again.';
    }
    
    if (error.message.includes('Failed to fetch')) {
      return 'Network error. Please check your connection.';
    }
    
    return 'An unexpected error occurred. Please try again.';
  }
}

// Create and export a singleton instance
const apiService = new ApiService();
export default apiService;
